import React, { useEffect, useState } from "react";
import "./styles.css";
import {
    fetchSchedule,
    createEvent,
    updateEvent as updateEventAPI,
    deleteEvent,
} from "./services/api";

// Hours for the daily view
const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);

// Days of the week
const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

// Months
const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

// Color options for events - improved visibility and contrast
const colorOptions = [
    "#2E8B57", // Sea Green - darker green for better contrast
    "#1976D2", // Blue - darker blue 
    "#D32F2F", // Red - slightly darker red
    "#F57C00", // Orange - darker orange
    "#7B1FA2", // Purple - darker purple
    "#0097A7", // Cyan - darker cyan
    "#5D4037", // Brown - darker brown
    "#455A64", // Blue Grey - darker blue grey
    "#C62828", // Dark Red
    "#1565C0", // Dark Blue
    "#2E7D32", // Dark Green
    "#EF6C00", // Dark Orange
];

// Predefined users with unique, vibrant colors for better distinction
const predefinedUsers = [
    { id: "user1", name: "Michael", color: "#FF3333", avatar: "👤" }, // Bright Red
    { id: "user2", name: "Domenic", color: "#2563EB", avatar: "👤" }, // Bright Blue
    { id: "user3", name: "Anton", color: "#22C55E", avatar: "👤" }, // Bright Green
    { id: "user4", name: "Toni", color: "#A855F7", avatar: "👤" }, // Bright Purple
    { id: "user5", name: "Willson", color: "#FF8C00", avatar: "👤" }, // Bright Orange
    { id: "user6", name: "Oscar", color: "#06B6D4", avatar: "👤" }, // Bright Cyan
    { id: "user7", name: "Rocco", color: "#EC4899", avatar: "👤" }, // Bright Pink
    { id: "user8", name: "Wendel", color: "#84CC16", avatar: "👤" }, // Bright Lime
    { id: "user9", name: "Mark", color: "#14B8A6", avatar: "👤" }, // Bright Teal
    { id: "user10", name: "Vishnu", color: "#FBBF24", avatar: "👤" }, // Bright Amber/Yellow
    { id: "user11", name: "Marin", color: "#1E40AF", avatar: "👤" }, // Deep Navy Blue
    { id: "user12", name: "Paul", color: "#eb9b34", avatar: "👤" }, // Dark Orange
    { id: "user13", name: "Luigi", color: "#3aeb34", avatar: "👤" }, // Light Green
    { id: "user14", name: "Rodney", color: "#32a8a2", avatar: "👤" }, // Baby Blue
];

// Helper function to convert Date to local YYYY-MM-DD string (no UTC conversion)
const toLocalDateKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export default function ScheduleBuilder() {
    const [schedule, setSchedule] = useState([]);

    // Add safety wrapper for setSchedule to filter out invalid events
    const setSafeSchedule = (newSchedule) => {
        if (Array.isArray(newSchedule)) {
            const validEvents = newSchedule.filter(event =>
                event &&
                (event.course || event.id || event._id) &&
                event.date
            );
            console.log(`setSafeSchedule: Filtered ${newSchedule.length} to ${validEvents.length} valid events`);
            setSchedule(validEvents);
        } else if (typeof newSchedule === 'function') {
            setSchedule(prevSchedule => {
                const updated = newSchedule(prevSchedule);
                if (Array.isArray(updated)) {
                    const validEvents = updated.filter(event =>
                        event &&
                        (event.course || event.id || event._id) &&
                        event.date
                    );
                    console.log(`setSafeSchedule (function): Filtered ${updated.length} to ${validEvents.length} valid events`);
                    return validEvents;
                }
                return updated;
            });
        } else {
            setSchedule(newSchedule);
        }
    };
    const [formData, setFormData] = useState({
        course: "",
        day: "Monday",
        date: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
        endDate: new Date().toISOString().split("T")[0], // End date for multiday events
        startTime: "",
        endTime: "",
        description: "",
        recurring: false,
        recurrenceType: "weekly", // weekly, monthly, yearly
        repeatWeeks: 1, // Number of weeks to repeat (for weekly recurrence)
        repeatLimited: false, // Whether to limit the number of repetitions
        isMultiDay: false, // Whether this is a multiday event
        allDay: false, // Whether this is an all-day event
        isOvernightEvent: false, // Whether this is an overnight event (spans midnight)
        excludedDates: [], // Array of excluded dates for multiday events
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState("week"); // week, month, year, list
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [editingEvent, setEditingEvent] = useState(null); // Track which event is being edited
    const [eventDetailsPopup, setEventDetailsPopup] = useState({ show: false, event: null, position: { x: 0, y: 0 } }); // Event details popup
    const [mobileWeekDayIndex, setMobileWeekDayIndex] = useState(0); // Mobile: which day of the week to show (0-6)
    // Use first user (Micheal) as default
    const currentUser = predefinedUsers[0];
    const [visibleUsers, setVisibleUsers] = useState(() => {
        // By default, show all users (including Paul and Luigi)
        const allUserIds = predefinedUsers.map(u => u.id);
        console.log('🎯 Initializing visible users:', allUserIds);
        return allUserIds;
    });
    const [selectedUsersForEvent, setSelectedUsersForEvent] = useState([predefinedUsers[0].id]); // For multi-user event creation
    const [searchQuery, setSearchQuery] = useState(""); // Search query for list view
    const [listViewFilter, setListViewFilter] = useState("upcoming"); // "upcoming" or "all"
    const [expandedUsers, setExpandedUsers] = useState(() => {
        // By default, expand all users
        return new Set(predefinedUsers.map(u => u.id));
    });
    const [selectedListUser, setSelectedListUser] = useState(null); // null = show all users

    // Load from API and check status
    useEffect(() => {
        const initializeApp = async () => {
            try {
                setLoading(true);
                setError(""); // Clear any previous errors

                console.log("Fetching schedule data...");
                const data = await fetchSchedule();
                console.log(`Received ${data.length} events`);

                // Map and clean data to ensure compatibility with existing code
                const formattedData = data
                    .filter(event => event && event.course) // Remove any undefined/invalid events
                    .map((event) => {
                        // Get user's color from predefinedUsers
                        const user = predefinedUsers.find(u => u.id === event.createdBy);
                        const userColor = user ? user.color : '#999999';

                        return {
                            ...event,
                            id: event._id || event.id || Date.now().toString(), // Ensure both id and _id are available
                            color: event.color || userColor, // Ensure color is always set (use user color or fallback)
                            course: event.course || 'Untitled Event', // Ensure course is always set
                            date: event.date || new Date().toISOString().split("T")[0], // Ensure date is set
                        };
                    });

                console.log(`Setting schedule with ${formattedData.length} valid events`);
                setSafeSchedule(formattedData);
            } catch (err) {
                console.error("Error loading schedule:", err);
                setError(`Failed to load schedule data: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        initializeApp();
    }, []);

    // Toggle user visibility
    const toggleUserVisibility = (userId) => {
        setVisibleUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => {
            const newData = {
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            };

            // Auto-update endDate when date changes for multiday events
            if (name === "date" && prev.isMultiDay && (!prev.endDate || new Date(prev.endDate) < new Date(value))) {
                newData.endDate = value;
            }

            // Clear endDate when multiday is disabled
            if (name === "isMultiDay" && !checked) {
                newData.endDate = prev.date;
            }

            // Auto-detect overnight events when times are entered
            if ((name === "startTime" || name === "endTime") && !prev.allDay && !prev.isMultiDay) {
                const startTime = name === "startTime" ? value : prev.startTime;
                const endTime = name === "endTime" ? value : prev.endTime;

                if (startTime && endTime && startTime >= endTime && !prev.isOvernightEvent) {
                    // Suggest this might be an overnight event
                    setError("Tip: If this is an overnight event (ending the next day), please check 'Overnight event'");
                } else if (startTime && endTime && startTime < endTime && prev.isOvernightEvent) {
                    // Times are normal, but overnight is still checked - turn it off
                    newData.isOvernightEvent = false;
                    setError("");
                } else if (startTime && endTime) {
                    setError("");
                }
            }

            return newData;
        });
    };

    const timesOverlap = (start1, end1, start2, end2, isOvernight1 = false, isOvernight2 = false) => {
        // Handle overnight events
        if (isOvernight1 && end1 < start1) {
            // First event is overnight (e.g., 22:00 to 06:00)
            if (isOvernight2 && end2 < start2) {
                // Both events are overnight
                return !(start1 >= end2 && start2 >= end1);
            } else {
                // First is overnight, second is normal
                return !(end1 <= start2 && start1 >= end2) || start2 >= start1 || end2 <= end1;
            }
        } else if (isOvernight2 && end2 < start2) {
            // Second event is overnight, first is normal
            return !(end2 <= start1 && start2 >= end1) || start1 >= start2 || end1 <= end2;
        } else {
            // Both events are normal (not overnight)
            return !(end1 <= start2 || start1 >= end2);
        }
    };

    const datesMatch = (date1, date2, recurrenceType, repeatWeeks = null, repeatLimited = false, endDate1 = null, excludedDates = []) => {
        // Create date objects at noon to avoid timezone issues
        const eventDate = new Date(date1 + 'T12:00:00');
        const targetDate = new Date(date2 + 'T12:00:00');

        // Check if the target date is excluded (using local date comparison)
        if (excludedDates && excludedDates.includes(date2)) {
            return false;
        }

        // Helper function to check if a date falls within a multiday range
        const isDateInMultidayRange = (startDate, endDate, target) => {
            const start = new Date(startDate + 'T12:00:00');
            const end = new Date(endDate + 'T12:00:00');
            return target >= start && target <= end;
        };

        // For non-recurring multiday events, check if target date falls within the range
        if (endDate1 && !recurrenceType) {
            return isDateInMultidayRange(date1, endDate1, targetDate);
        }

        // For single-day non-recurring events - exact date match
        if (!recurrenceType && !endDate1) {
            return eventDate.toDateString() === targetDate.toDateString();
        }

        // For recurring events
        if (recurrenceType === "weekly") {
            const daysDifference = Math.floor((targetDate - eventDate) / (1000 * 60 * 60 * 24));

            // If it's a limited recurring event, check if target date is within the repeat range
            if (repeatLimited && repeatWeeks) {
                const weeksDifference = Math.floor(daysDifference / 7);
                if (weeksDifference >= repeatWeeks) {
                    return false;
                }
            }

            // For multiday recurring events
            if (endDate1) {
                const eventEndDate = new Date(endDate1 + 'T12:00:00');
                const eventDuration = Math.floor((eventEndDate - eventDate) / (1000 * 60 * 60 * 24));

                // Check each weekly occurrence
                for (let week = 0; !repeatLimited || week < repeatWeeks; week++) {
                    const occurrenceStart = new Date(eventDate);
                    occurrenceStart.setDate(eventDate.getDate() + (week * 7));

                    const occurrenceEnd = new Date(occurrenceStart);
                    occurrenceEnd.setDate(occurrenceStart.getDate() + eventDuration);

                    if (targetDate >= occurrenceStart && targetDate <= occurrenceEnd) {
                        return true;
                    }

                    // If we've gone past the target date and it's unlimited repetition, stop checking
                    if (!repeatLimited && occurrenceStart > targetDate) {
                        break;
                    }
                }
                return false;
            } else {
                // Single-day recurring events - check if same day of week
                if (daysDifference < 0) return false;

                const eventDayOfWeek = eventDate.getDay();
                const targetDayOfWeek = targetDate.getDay();

                return eventDayOfWeek === targetDayOfWeek;
            }
        } else if (recurrenceType === "monthly") {
            // For multiday monthly recurring events
            if (endDate1) {
                const eventEndDate = new Date(endDate1 + 'T12:00:00');
                const eventDuration = Math.floor((eventEndDate - eventDate) / (1000 * 60 * 60 * 24));
                const targetMonth = targetDate.getMonth();
                const targetYear = targetDate.getFullYear();

                // Check if target date falls within this month's occurrence
                const monthlyStart = new Date(targetYear, targetMonth, eventDate.getDate(), 12);
                const monthlyEnd = new Date(monthlyStart);
                monthlyEnd.setDate(monthlyStart.getDate() + eventDuration);

                return targetDate >= monthlyStart && targetDate <= monthlyEnd;
            } else {
                return eventDate.getDate() === targetDate.getDate();
            }
        } else if (recurrenceType === "yearly") {
            // For multiday yearly recurring events
            if (endDate1) {
                const eventEndDate = new Date(endDate1 + 'T12:00:00');
                const eventDuration = Math.floor((eventEndDate - eventDate) / (1000 * 60 * 60 * 24));
                const targetYear = targetDate.getFullYear();

                // Check if target date falls within this year's occurrence
                const yearlyStart = new Date(targetYear, eventDate.getMonth(), eventDate.getDate(), 12);
                const yearlyEnd = new Date(yearlyStart);
                yearlyEnd.setDate(yearlyStart.getDate() + eventDuration);

                return targetDate >= yearlyStart && targetDate <= yearlyEnd;
            } else {
                return eventDate.getMonth() === targetDate.getMonth() && eventDate.getDate() === targetDate.getDate();
            }
        }

        return false;
    };

    const addToSchedule = async () => {
        const { course, date, endDate, startTime, endTime, isMultiDay, allDay } = formData;

        // If we're editing an event, call updateEvent instead
        if (editingEvent) {
            return updateEvent();
        }

        if (!course || !date) {
            setError("Please fill out all required fields.");
            return;
        }

        // Validation for multiday events
        if (isMultiDay) {
            if (!endDate) {
                setError("Please select an end date for multiday events.");
                return;
            }
            if (new Date(endDate) < new Date(date)) {
                setError("End date must be after or equal to start date.");
                return;
            }
            if (!allDay && (!startTime || !endTime)) {
                setError("Please fill out start and end times for timed multiday events.");
                return;
            }
        } else {
            // Single day event validation
            if (!allDay && (!startTime || !endTime)) {
                setError("Please fill out start and end times.");
                return;
            }
        }

        // Handle overnight events (only for single day events)
        // Multi-day events can have any time combination since they span multiple days
        if (!allDay && startTime && endTime && !isMultiDay) {
            if (startTime >= endTime && !formData.isOvernightEvent) {
                setError("This appears to be an overnight event. Please check 'Overnight Event' to confirm.");
                return;
            }
        }

        // Check for conflicts
        // For multi-user support, only check conflicts for the SELECTED users, not all users
        // This allows different users to have different events on the same day
        const usersToCheck = selectedUsersForEvent.length > 0 ? selectedUsersForEvent : [currentUser.id];

        const conflict = schedule.some((item) => {
            // Only check conflicts against events from users we're trying to schedule for
            if (!usersToCheck.includes(item.createdBy)) {
                return false; // Not a conflict if event is from a different user
            }
            // Simple function to check if a specific date falls within an event's range
            const isDateInEventRange = (checkDate, eventStartDate, eventEndDate) => {
                const check = new Date(checkDate);
                const start = new Date(eventStartDate);
                const end = eventEndDate ? new Date(eventEndDate) : start;

                // Normalize to compare dates only (ignore time)
                check.setHours(12, 0, 0, 0);
                start.setHours(12, 0, 0, 0);
                end.setHours(12, 0, 0, 0);

                return check >= start && check <= end;
            };

            // Check if there's any date overlap between the new event and existing event
            let hasDateOverlap = false;

            if (item.recurring) {
                // For recurring events, check if the new event's dates match any recurrence
                if (isMultiDay && endDate) {
                    // Check each day of the new multiday event
                    const newStart = new Date(date);
                    const newEnd = new Date(endDate);

                    for (let d = new Date(newStart); d <= newEnd; d.setDate(d.getDate() + 1)) {
                        if (datesMatch(
                            d.toISOString().split("T")[0],
                            item.date,
                            item.recurrenceType,
                            item.repeatWeeks,
                            item.repeatLimited,
                            item.endDate,
                            item.excludedDates
                        )) {
                            hasDateOverlap = true;
                            break;
                        }
                    }
                } else {
                    // Single day event against recurring event
                    hasDateOverlap = datesMatch(
                        date,
                        item.date,
                        item.recurrenceType,
                        item.repeatWeeks,
                        item.repeatLimited,
                        item.endDate,
                        item.excludedDates
                    );
                }
            } else {
                // For non-recurring events, check direct date overlap
                if (isMultiDay && endDate) {
                    // New event is multiday - check if any day overlaps with existing event
                    const newStart = new Date(date);
                    const newEnd = new Date(endDate);

                    for (let d = new Date(newStart); d <= newEnd; d.setDate(d.getDate() + 1)) {
                        if (isDateInEventRange(d.toISOString().split("T")[0], item.date, item.endDate)) {
                            hasDateOverlap = true;
                            break;
                        }
                    }
                } else {
                    // New event is single day - check if it falls within existing event's range
                    hasDateOverlap = isDateInEventRange(date, item.date, item.endDate);
                }
            }

            // If no date overlap, no conflict
            if (!hasDateOverlap) {
                return false;
            }

            // If there's date overlap, check time overlap
            if (allDay || item.allDay) {
                return true; // All-day events conflict if dates overlap
            }

            // For timed events, check time overlap
            if (startTime && endTime && item.startTime && item.endTime) {
                return timesOverlap(startTime, endTime, item.startTime, item.endTime,
                    formData.isOvernightEvent, item.isOvernightEvent);
            }

            // If dates overlap but times are missing, assume conflict for safety
            return true;
        });

        if (conflict) {
            setError("Schedule conflict detected!");
            return;
        }

        // Set the day of week based on the selected date
        const dayOfWeek =
            daysOfWeek[
            new Date(date).getDay() === 0 ? 6 : new Date(date).getDay() - 1
            ];

        // For multiday events, create a day range description
        const dayRange = isMultiDay && endDate ?
            `${dayOfWeek} - ${daysOfWeek[new Date(endDate).getDay() === 0 ? 6 : new Date(endDate).getDay() - 1]}` :
            dayOfWeek;

        try {
            setLoading(true);

            // Create events for all selected users
            const usersToCreate = selectedUsersForEvent.length > 0 ? selectedUsersForEvent : [currentUser.id];
            const savedEvents = [];

            for (const userId of usersToCreate) {
                const user = predefinedUsers.find(u => u.id === userId);
                if (!user) continue;

                // Create the new event object for this user
                const newEvent = {
                    ...formData,
                    day: isMultiDay ? dayRange : dayOfWeek,
                    // Ensure endDate is only set for multiday events
                    endDate: isMultiDay ? formData.endDate : null,
                    // Set default times for all-day events
                    startTime: allDay ? "00:00" : formData.startTime,
                    endTime: allDay ? "23:59" : formData.endTime,
                    // Use user's color for event color
                    color: user.color,
                    // Add user information
                    createdBy: user.id,
                    createdByName: user.name,
                    userColor: user.color,
                    userAvatar: user.avatar,
                };

                // Send to API and get the saved event with MongoDB _id
                const savedEvent = await createEvent(newEvent);
                savedEvents.push(savedEvent);
            }

            // Update the local state with all saved events
            setSafeSchedule((prev) => [...prev, ...savedEvents]);

            // Reset form with current date
            const currentDate = new Date().toISOString().split("T")[0];
            setFormData({
                course: "",
                day: dayOfWeek,
                date: currentDate,
                endDate: currentDate,
                startTime: "",
                endTime: "",
                description: "",
                recurring: false,
                recurrenceType: "weekly",
                repeatWeeks: 1,
                repeatLimited: false,
                isMultiDay: false,
                allDay: false,
                isOvernightEvent: false,
                excludedDates: [],
            });

            // Reset selected users to current user
            setSelectedUsersForEvent([currentUser.id]);
            setError("");
        } catch (err) {
            console.error("Error adding to schedule:", err);
            setError("Failed to add event. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Clear the schedule
    // Remove a specific event
    const removeEvent = async (id) => {
        try {
            setLoading(true);
            // Use the MongoDB _id if available, otherwise use the id
            const eventId = id.toString().includes("ObjectId") ? id : id;
            await deleteEvent(eventId);
            setSafeSchedule((prev) =>
                prev.filter((event) => event._id !== id && event.id !== id)
            );
        } catch (err) {
            console.error("Error removing event:", err);
            setError("Failed to remove event. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Update an existing event
    const updateEvent = async () => {
        console.log('🔵 updateEvent called');
        const { course, date, endDate, startTime, endTime, isMultiDay, allDay } = formData;

        if (!course || !date) {
            console.log('❌ Missing required fields:', { course, date });
            setError("Please fill out all required fields.");
            return;
        }

        console.log('✅ Validation passed, preparing update...');
        console.log('📋 Form data:', { course, date, endDate, startTime, endTime, isMultiDay, allDay });

        // Validation for multiday events
        if (isMultiDay) {
            console.log('📅 Validating multiday event...');
            if (!endDate) {
                console.log('❌ No end date');
                setError("Please select an end date for multiday events.");
                return;
            }
            if (new Date(endDate) < new Date(date)) {
                console.log('❌ End date before start date');
                setError("End date must be after or equal to start date.");
                return;
            }
            if (!allDay && (!startTime || !endTime)) {
                console.log('❌ Missing times for timed multiday');
                setError("Please fill out start and end times for timed multiday events.");
                return;
            }
            console.log('✅ Multiday validation passed');
        } else {
            // Single day event validation
            console.log('📅 Validating single-day event...');
            if (!allDay && (!startTime || !endTime)) {
                console.log('❌ Missing times:', { startTime, endTime, allDay });
                setError("Please fill out start and end times.");
                return;
            }
            console.log('✅ Single-day validation passed');
        }

        // Handle overnight events (only for single day events)
        // Multi-day events can have any time combination since they span multiple days
        if (!allDay && startTime && endTime && !isMultiDay) {
            if (startTime >= endTime && !formData.isOvernightEvent) {
                console.log('❌ Overnight event check failed');
                setError("This appears to be an overnight event. Please check 'Overnight Event' to confirm.");
                return;
            }
        }

        console.log('✅ Overnight check passed, checking conflicts...');

        // Check for conflicts with other events (excluding the event being edited)
        // Skip conflict check if we're editing - the user is just updating the same event
        let conflict = false;
        if (!editingEvent) {
            console.log('🔍 Checking conflicts for new event...');
            conflict = schedule.some((item) => {
                console.log('🔎 Checking conflict for:', item.course, 'id:', item._id || item.id);

                // Same conflict checking logic as addToSchedule
                const isDateInEventRange = (checkDate, eventStartDate, eventEndDate) => {
                    const check = new Date(checkDate);
                    const start = new Date(eventStartDate);
                    const end = eventEndDate ? new Date(eventEndDate) : start;

                    check.setHours(12, 0, 0, 0);
                    start.setHours(12, 0, 0, 0);
                    end.setHours(12, 0, 0, 0);

                    return check >= start && check <= end;
                };

                let hasDateOverlap = false;

                if (item.recurring) {
                    if (isMultiDay && endDate) {
                        const newStart = new Date(date);
                        const newEnd = new Date(endDate);

                        for (let d = new Date(newStart); d <= newEnd; d.setDate(d.getDate() + 1)) {
                            if (datesMatch(
                                d.toISOString().split("T")[0],
                                item.date,
                                item.recurrenceType,
                                item.repeatWeeks,
                                item.repeatLimited,
                                item.endDate,
                                item.excludedDates
                            )) {
                                hasDateOverlap = true;
                                break;
                            }
                        }
                    } else {
                        hasDateOverlap = datesMatch(
                            date,
                            item.date,
                            item.recurrenceType,
                            item.repeatWeeks,
                            item.repeatLimited,
                            item.endDate,
                            item.excludedDates
                        );
                    }
                } else {
                    if (isMultiDay && endDate) {
                        const newStart = new Date(date);
                        const newEnd = new Date(endDate);

                        for (let d = new Date(newStart); d <= newEnd; d.setDate(d.getDate() + 1)) {
                            if (isDateInEventRange(d.toISOString().split("T")[0], item.date, item.endDate)) {
                                hasDateOverlap = true;
                                break;
                            }
                        }
                    } else {
                        hasDateOverlap = isDateInEventRange(date, item.date, item.endDate);
                    }
                }

                if (!hasDateOverlap) {
                    return false;
                }

                if (allDay || item.allDay) {
                    return true;
                }

                if (startTime && endTime && item.startTime && item.endTime) {
                    return timesOverlap(startTime, endTime, item.startTime, item.endTime,
                        formData.isOvernightEvent, item.isOvernightEvent);
                }

                return true;
            });
        } else {
            console.log('✅ Skipping conflict check - editing existing event');
        }

        if (conflict) {
            console.log('❌ Schedule conflict detected!');
            setError("Schedule conflict detected!");
            return;
        }

        console.log('✅ No conflicts, proceeding with update...');

        const dayOfWeek = daysOfWeek[new Date(date).getDay() === 0 ? 6 : new Date(date).getDay() - 1];
        const dayRange = isMultiDay && endDate ?
            `${dayOfWeek} - ${daysOfWeek[new Date(endDate).getDay() === 0 ? 6 : new Date(endDate).getDay() - 1]}` :
            dayOfWeek;

        try {
            console.log('🔄 Starting update process...');
            setLoading(true);

            const updatedEventData = {
                ...formData,
                day: isMultiDay ? dayRange : dayOfWeek,
                endDate: isMultiDay ? formData.endDate : null,
                startTime: allDay ? "00:00" : formData.startTime,
                endTime: allDay ? "23:59" : formData.endTime,
            };

            // Find the specific event being edited by ID
            const eventsToUpdate = schedule.filter(
                (item) => item._id === editingEvent._id || item.id === editingEvent.id
            );

            // Update all instances of this event
            const updatedSchedule = [...schedule];
            for (const eventToUpdate of eventsToUpdate) {
                const updatedEvent = {
                    ...eventToUpdate,
                    ...updatedEventData,
                    createdBy: eventToUpdate.createdBy,
                    createdByName: eventToUpdate.createdByName,
                    userColor: eventToUpdate.userColor,
                    color: eventToUpdate.color,
                };

                // Update via API
                await updateEventAPI(eventToUpdate._id || eventToUpdate.id, updatedEvent);

                // Update in local state
                const index = updatedSchedule.findIndex(
                    (e) => e._id === eventToUpdate._id || e.id === eventToUpdate.id
                );
                if (index !== -1) {
                    updatedSchedule[index] = updatedEvent;
                }
            }

            setSafeSchedule(updatedSchedule);

            // Reset form and exit edit mode
            cancelEdit();
        } catch (err) {
            console.error("Error updating event:", err);
            setError("Failed to update event. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Edit an existing event
    const editEvent = (event) => {
        // Safety check for undefined event
        if (!event) {
            console.error("Cannot edit undefined event");
            setError("Cannot edit event - event data is missing");
            return;
        }

        console.log("Editing event:", event);

        setFormData({
            course: event.course || "",
            day: event.day || "Monday",
            date: event.date || new Date().toISOString().split("T")[0],
            endDate: event.endDate || event.date || new Date().toISOString().split("T")[0],
            startTime: event.startTime || "",
            endTime: event.endTime || "",
            description: event.description || "",
            color: event.color || colorOptions[0],
            recurring: event.recurring || false,
            recurrenceType: event.recurrenceType || "weekly",
            repeatWeeks: event.repeatWeeks || 1,
            repeatLimited: event.repeatLimited || false,
            isMultiDay: event.isMultiDay || false,
            allDay: event.allDay || false,
            isOvernightEvent: event.isOvernightEvent || false,
            excludedDates: event.excludedDates || [],
        });
        setEditingEvent(event);
        setError("");
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingEvent(null);
        const currentDate = new Date().toISOString().split("T")[0];
        setFormData({
            course: "",
            day: "Monday",
            date: currentDate,
            endDate: currentDate,
            startTime: "",
            endTime: "",
            description: "",
            color: colorOptions[Math.floor(Math.random() * colorOptions.length)],
            recurring: false,
            recurrenceType: "weekly",
            repeatWeeks: 1,
            repeatLimited: false,
            isMultiDay: false,
            allDay: false,
            excludedDates: [],
        });
        setError("");
    };

    // Show event details popup
    const showEventDetails = (event, mouseEvent) => {
        if (!event) return;

        const rect = mouseEvent.target.getBoundingClientRect();
        setEventDetailsPopup({
            show: true,
            event: event,
            position: {
                x: rect.left + rect.width / 2,
                y: rect.top - 10
            }
        });
    };

    // Hide event details popup
    const hideEventDetails = () => {
        setEventDetailsPopup({ show: false, event: null, position: { x: 0, y: 0 } });
    };

    // Exclude a specific date from a multiday event
    const excludeDateFromEvent = async (eventId, dateToExclude) => {
        try {
            setLoading(true);

            // Find the event to update
            const eventToUpdate = schedule.find(event =>
                event._id === eventId || event.id === eventId
            );

            if (!eventToUpdate) {
                setError("Event not found.");
                return;
            }

            // Add the date to excluded dates
            const updatedExcludedDates = [
                ...(eventToUpdate.excludedDates || []),
                dateToExclude
            ];



            // Update via API (we'll need to add this endpoint)
            // For now, update locally
            setSafeSchedule((prev) =>
                prev.map((event) =>
                    event._id === eventId || event.id === eventId
                        ? { ...event, excludedDates: updatedExcludedDates }
                        : event
                )
            );

            setError("");
        } catch (err) {
            console.error("Error excluding date from event:", err);
            setError("Failed to exclude date. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Restore a specific date to a multiday event
    const restoreDateToEvent = async (eventId, dateToRestore) => {
        try {
            setLoading(true);

            // Find the event to update
            const eventToUpdate = schedule.find(event =>
                event._id === eventId || event.id === eventId
            );

            if (!eventToUpdate) {
                setError("Event not found.");
                return;
            }

            // Remove the date from excluded dates
            const updatedExcludedDates = (eventToUpdate.excludedDates || []).filter(
                date => date !== dateToRestore
            );

            // Update locally
            setSafeSchedule((prev) =>
                prev.map((event) =>
                    event._id === eventId || event.id === eventId
                        ? { ...event, excludedDates: updatedExcludedDates }
                        : event
                )
            );

            setError("");
        } catch (err) {
            console.error("Error restoring date to event:", err);
            setError("Failed to restore date. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Get events for a specific date
    const getEventsForDate = (date) => {
        return schedule.filter((event) => {
            if (event.recurring) {
                return datesMatch(
                    event.date,
                    toLocalDateKey(date),
                    event.recurrenceType,
                    event.repeatWeeks,
                    event.repeatLimited,
                    event.endDate,
                    event.excludedDates
                );
            } else {
                return datesMatch(
                    event.date,
                    toLocalDateKey(date),
                    null,
                    null,
                    false,
                    event.endDate,
                    event.excludedDates
                );
            }
        });
    };

    // Generate days for the month view
    const getDaysInMonth = (year, month) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Adjust for Monday start

        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < adjustedFirstDay; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    // Navigate to previous/next week, month, or year
    const navigatePeriod = (direction) => {
        if (view === "week") {
            const newDate = new Date(selectedDate);
            newDate.setDate(newDate.getDate() + direction * 7);
            setSelectedDate(newDate);
        } else if (view === "month") {
            let newMonth = selectedMonth + direction;
            let newYear = selectedYear;

            if (newMonth < 0) {
                newMonth = 11;
                newYear--;
            } else if (newMonth > 11) {
                newMonth = 0;
                newYear++;
            }

            setSelectedMonth(newMonth);
            setSelectedYear(newYear);
        } else if (view === "year") {
            setSelectedYear(selectedYear + direction);
        }
    };

    // Get the start and end dates of the current week
    const getWeekDates = () => {
        const date = new Date(selectedDate);
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Calculate the date of Monday in this week
        const mondayDate = new Date(date);
        const daysFromMonday = day === 0 ? 6 : day - 1; // Sunday is 6 days from Monday
        mondayDate.setDate(date.getDate() - daysFromMonday);

        // Calculate the date of Sunday in this week  
        const sundayDate = new Date(mondayDate);
        sundayDate.setDate(mondayDate.getDate() + 6);

        return { mondayDate, sundayDate };
    };

    // Format date for display
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    // Format date string (YYYY-MM-DD) without timezone issues
    const formatDateString = (dateStr) => {
        if (!dateStr) return "";
        // Extract just the YYYY-MM-DD part if it's an ISO string
        const datePart = dateStr.split("T")[0];
        const [year, month, day] = datePart.split("-");
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
    };

    // Get week days for the week view
    const getWeekDays = () => {
        const { mondayDate } = getWeekDates();
        const weekDays = [];

        for (let i = 0; i < 7; i++) {
            const day = new Date(mondayDate);
            day.setDate(mondayDate.getDate() + i);
            weekDays.push(day);
        }

        return weekDays;
    };

    // Check if a date is today
    const isToday = (date) => {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    // Format time for display
    const formatTime = (timeString) => {
        if (!timeString) return "Time not set";

        const [hours, minutes] = timeString.split(":");
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    // Get events for a specific date as continuous blocks
    const getEventsForDay = (date) => {
        const targetDateStr = toLocalDateKey(date);

        return schedule.filter((event) => {
            if (!event) return false;

            // Filter by visible users
            if (event.createdBy && !visibleUsers.includes(event.createdBy)) {
                return false;
            }

            // Check if this date matches the event start date
            const matchesStartDate = datesMatch(
                event.date,
                targetDateStr,
                event.recurring ? event.recurrenceType : null,
                event.repeatWeeks,
                event.repeatLimited,
                event.endDate,
                event.excludedDates
            );

            if (matchesStartDate) return true;

            // For overnight events, also check if this is the next day after any occurrence
            if (event.isOvernightEvent && event.startTime && event.endTime) {
                const [startHour, startMin] = event.startTime.split(':').map(Number);
                const [endHour, endMin] = event.endTime.split(':').map(Number);
                const startDecimal = startHour + startMin / 60;
                const endDecimal = endHour + endMin / 60;

                // If end time is before start time, it's an overnight event
                if (endDecimal < startDecimal) {
                    const eventDate = new Date(event.date + 'T12:00:00');
                    const targetDate = new Date(targetDateStr + 'T12:00:00');

                    // For recurring events, check each occurrence
                    if (event.recurring && event.recurrenceType === "weekly") {
                        const daysDifference = Math.floor((targetDate - eventDate) / (1000 * 60 * 60 * 24));

                        // Check if target is exactly 1 day after any weekly occurrence
                        if (daysDifference > 0 && (daysDifference - 1) % 7 === 0) {
                            // This is the day after a weekly occurrence
                            if (!event.repeatLimited || Math.floor((daysDifference - 1) / 7) < event.repeatWeeks) {
                                return true;
                            }
                        }
                    } else {
                        // For non-recurring overnight events, check if target is the next day
                        const nextDay = new Date(eventDate);
                        nextDay.setDate(eventDate.getDate() + 1);

                        if (targetDate.toDateString() === nextDay.toDateString()) {
                            return true;
                        }
                    }
                }
            }

            return false;
        }).sort((a, b) => {
            // Sort by start time
            const timeA = a.startTime || "00:00";
            const timeB = b.startTime || "00:00";
            return timeA.localeCompare(timeB);
        });
    };

    // Calculate continuous event style (position and height)
    // Calculate overlapping events for better layout
    const getEventOverlapInfo = (event, viewingDate, allDayEvents) => {
        const eventDate = toLocalDateKey(viewingDate);

        // Get events for this day
        const dayEvents = allDayEvents.filter(e => {
            const eDate = toLocalDateKey(new Date(e.date));
            return eDate === eventDate && !e.allDay && e.startTime && e.endTime;
        });

        if (!event.startTime || !event.endTime) return { index: 0, total: 1 };

        const [startHour, startMin] = event.startTime.split(':').map(Number);
        const [endHour, endMin] = event.endTime.split(':').map(Number);
        const startDecimal = startHour + startMin / 60;
        const endDecimal = endHour + endMin / 60;

        // Find overlapping events
        const overlapping = dayEvents.filter(other => {
            if (other.id === event.id || other._id === event._id) return false;
            const [oStartHour, oStartMin] = other.startTime.split(':').map(Number);
            const [oEndHour, oEndMin] = other.endTime.split(':').map(Number);
            const oStartDecimal = oStartHour + oStartMin / 60;
            const oEndDecimal = oEndHour + oEndMin / 60;
            // Check if times overlap
            return !(endDecimal <= oStartDecimal || startDecimal >= oEndDecimal);
        });

        const allOverlapping = [event, ...overlapping];
        const sortedEvents = allOverlapping.sort((a, b) => {
            const [aStart] = a.startTime.split(':').map(Number);
            const [bStart] = b.startTime.split(':').map(Number);
            return aStart - bStart;
        });

        const index = sortedEvents.findIndex(e => e.id === event.id || e._id === event._id);
        return { index: index >= 0 ? index : 0, total: allOverlapping.length };
    };

    const getContinuousEventStyle = (event, viewingDate, allDayEvents = []) => {
        if (event.allDay) {
            return {
                top: '0',
                height: '100%',
                position: 'absolute'
            };
        }

        if (!event.startTime || !event.endTime) {
            return {
                top: '0',
                height: '100%',
                position: 'absolute'
            };
        }

        const [startHour, startMin] = event.startTime.split(':').map(Number);
        const [endHour, endMin] = event.endTime.split(':').map(Number);

        // Convert to decimal hours (0-24)
        const startDecimal = startHour + startMin / 60;
        const endDecimal = endHour + endMin / 60;

        // Handle overnight events
        if (event.isOvernightEvent && endDecimal < startDecimal) {
            const viewDateStr = toLocalDateKey(viewingDate);

            // Check if viewing date matches the event's start date (considering recurrence)
            const isStartDate = datesMatch(
                event.date,
                viewDateStr,
                event.recurring ? event.recurrenceType : null,
                event.repeatWeeks,
                event.repeatLimited,
                event.endDate,
                event.excludedDates
            );

            if (isStartDate) {
                // On start day: show from start time to midnight (24:00)
                const topPercent = (startDecimal / 24) * 100;
                const heightPercent = ((24 - startDecimal) / 24) * 100;

                const overlapInfo = getEventOverlapInfo(event, viewingDate, allDayEvents);
                const width = 100 / Math.max(overlapInfo.total, 1);
                const left = (overlapInfo.index * width);

                return {
                    top: `${topPercent}%`,
                    height: `${Math.max(heightPercent, 2)}%`,
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`
                };
            } else {
                // On next day: show from midnight (0:00) to end time
                // This handles both single occurrence and recurring overnight events
                const topPercent = 0;
                const heightPercent = (endDecimal / 24) * 100;

                const overlapInfo = getEventOverlapInfo(event, viewingDate, allDayEvents);
                const width = 100 / Math.max(overlapInfo.total, 1);
                const left = (overlapInfo.index * width);

                return {
                    top: `${topPercent}%`,
                    height: `${Math.max(heightPercent, 2)}%`,
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`
                };
            }
        }

        // Regular events (not overnight)
        const topPercent = (startDecimal / 24) * 100;
        const heightPercent = ((endDecimal - startDecimal) / 24) * 100;

        // Calculate overlap positioning
        const overlapInfo = getEventOverlapInfo(event, viewingDate, allDayEvents);
        const width = 100 / Math.max(overlapInfo.total, 1);
        const left = (overlapInfo.index * width);

        return {
            top: `${topPercent}%`,
            height: `${Math.max(heightPercent, 2)}%`, // Minimum 2% height
            position: 'absolute',
            left: `${left}%`,
            width: `${width}%`
        };
    };

    return (
        <div className="schedule-builder" onClick={hideEventDetails}>
            <div className="header-section">
                <h1 className="app-title">Nexeya Calendar</h1>
                <p className="app-subtitle">The professional app to plan trips and meetings</p>
            </div>

            {loading && <div className="loading-indicator">Processing...</div>}

            {/* User Visibility Filters */}
            <div className="user-section">
                <div className="user-filters">
                    <label>Show Events From:</label>
                    <div className="user-filter-buttons">
                        {predefinedUsers.map(user => (
                            <button
                                key={user.id}
                                className={`user-filter-btn ${visibleUsers.includes(user.id) ? 'active' : ''}`}
                                onClick={() => toggleUserVisibility(user.id)}
                                style={{
                                    backgroundColor: visibleUsers.includes(user.id) ? user.color : '#ccc',
                                    opacity: visibleUsers.includes(user.id) ? 1 : 0.5
                                }}
                                title={`${visibleUsers.includes(user.id) ? 'Hide' : 'Show'} ${user.name}'s events`}
                            >
                                {user.avatar} {user.name}
                            </button>
                        ))}
                        <button
                            className="user-filter-btn all-btn"
                            onClick={() => setVisibleUsers(predefinedUsers.map(u => u.id))}
                        >
                            Show All
                        </button>
                    </div>
                </div>
            </div>

            <div className="view-controls">
                <button
                    className={view === "week" ? "active" : ""}
                    onClick={() => setView("week")}
                >
                    Week
                </button>
                <button
                    className={view === "month" ? "active" : ""}
                    onClick={() => setView("month")}
                >
                    Month
                </button>
                <button
                    className={view === "year" ? "active" : ""}
                    onClick={() => setView("year")}
                >
                    Year
                </button>
                <button
                    className={view === "list" ? "active" : ""}
                    onClick={() => setView("list")}
                >
                    List
                </button>
            </div>

            <div className="form-container">
                <div className="form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 1fr', gap: '16px', alignItems: 'start' }}>
                    {/* Column 1: Event Name and Description */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div className="form-group">
                            <label htmlFor="course">Event Name*</label>
                            <input
                                type="text"
                                id="course"
                                name="course"
                                placeholder="Enter event name"
                                value={formData.course}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                name="description"
                                placeholder="Add a description (optional)"
                                value={formData.description}
                                onChange={handleChange}
                                rows="10"
                                style={{ resize: 'vertical', minHeight: '240px' }}
                            />
                        </div>
                    </div>

                    {/* Column 2: Checkboxes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="isMultiDay"
                                    checked={formData.isMultiDay}
                                    onChange={handleChange}
                                />
                                Multi-day event
                            </label>
                        </div>

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="allDay"
                                    checked={formData.allDay}
                                    onChange={handleChange}
                                />
                                All-day event
                            </label>
                        </div>

                        {!formData.isMultiDay && (
                            <div className="form-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="isOvernightEvent"
                                        checked={formData.isOvernightEvent}
                                        onChange={handleChange}
                                    />
                                    Overnight event
                                </label>
                            </div>
                        )}

                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="recurring"
                                    checked={formData.recurring}
                                    onChange={handleChange}
                                />
                                Recurring event
                            </label>
                        </div>
                    </div>

                    {/* Column 3: Users Selection */}
                    <div className="form-group">
                        <label htmlFor="selectedUsers">Schedule for Users*</label>
                        <div className="user-multi-select" style={{ minHeight: '150px' }}>
                            {predefinedUsers.map(user => (
                                <label key={user.id} className="user-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedUsersForEvent.includes(user.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedUsersForEvent(prev => [...prev, user.id]);
                                            } else {
                                                setSelectedUsersForEvent(prev => prev.filter(id => id !== user.id));
                                            }
                                        }}
                                    />
                                    <span
                                        className="user-checkbox-label"
                                        style={{ backgroundColor: user.color, color: 'white', padding: '4px 4px', borderRadius: '4px', marginLeft: '4px', fontSize: '0.85rem' }}
                                    >
                                        {user.avatar} {user.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                            {selectedUsersForEvent.length} user(s) selected
                        </small>
                    </div>

                    {/* Column 4: Date and Time */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div className="form-group">
                            <label htmlFor="date">{formData.isMultiDay ? 'Start Date*' : 'Date*'}</label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {formData.isMultiDay && (
                            <div className="form-group">
                                <label htmlFor="endDate">End Date*</label>
                                <input
                                    type="date"
                                    id="endDate"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                    min={formData.date}
                                    required
                                />
                            </div>
                        )}

                        {/* Times - under dates */}
                        {!formData.allDay && (
                            <>
                                <div className="form-group">
                                    <label htmlFor="startTime">Start Time*</label>
                                    <input
                                        type="time"
                                        id="startTime"
                                        name="startTime"
                                        value={formData.startTime}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="endTime">End Time*</label>
                                    <input
                                        type="time"
                                        id="endTime"
                                        name="endTime"
                                        value={formData.endTime}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </>
                        )}
                    </div>



                    {formData.recurring && (
                        <>
                            <div className="form-group">
                                <label htmlFor="recurrenceType">Recurrence Pattern</label>
                                <select
                                    id="recurrenceType"
                                    name="recurrenceType"
                                    value={formData.recurrenceType}
                                    onChange={handleChange}
                                >
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                                {formData.isMultiDay && formData.recurrenceType === "weekly" && (
                                    <small>Multiday event will repeat weekly (entire duration each week)</small>
                                )}
                            </div>

                            {formData.recurrenceType === "weekly" && (
                                <>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="repeatLimited"
                                                checked={formData.repeatLimited}
                                                onChange={handleChange}
                                            />
                                            Limit repetitions to specific number of weeks
                                        </label>
                                    </div>

                                    {formData.repeatLimited && (
                                        <div className="form-group">
                                            <label htmlFor="repeatWeeks">Number of weeks to repeat</label>
                                            <input
                                                type="number"
                                                id="repeatWeeks"
                                                name="repeatWeeks"
                                                value={formData.repeatWeeks}
                                                onChange={handleChange}
                                                min="1"
                                                max="52"
                                                placeholder="Enter number of weeks"
                                            />
                                            <small>
                                                {formData.isMultiDay
                                                    ? `Multiday event will repeat for ${formData.repeatWeeks} week${formData.repeatWeeks !== 1 ? 's' : ''} (${formData.endDate && formData.date && new Date(formData.endDate) >= new Date(formData.date)
                                                        ? Math.ceil((new Date(formData.endDate) - new Date(formData.date)) / (1000 * 60 * 60 * 24)) + 1
                                                        : 'X'
                                                    } days each week)`
                                                    : `Event will repeat for ${formData.repeatWeeks} week${formData.repeatWeeks !== 1 ? 's' : ''} (including the original)`
                                                }
                                            </small>
                                        </div>
                                        )}
                                        </>
                                        )}
                        </>
                    )}

                    <div className="form-buttons">
                        <button onClick={editingEvent ? updateEvent : addToSchedule} className="btn btn-primary">
                            {editingEvent ? "Update Event" : "Add Event"}
                        </button>
                        {editingEvent && (
                            <button onClick={cancelEdit} className="btn btn-secondary">
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && <div className="error">{error}</div>}

            <h2>{view.charAt(0).toUpperCase() + view.slice(1)} View</h2>

            {/* Navigation controls for all views */}
            <div className="period-navigation">
                <button onClick={() => navigatePeriod(-1)}>
                    Previous{" "}
                    {view === "week" ? "Week" : view === "month" ? "Month" : "Year"}
                </button>
                {view === "week" && (
                    <span>
                        {formatDate(getWeekDates().mondayDate)} -{" "}
                        {formatDate(getWeekDates().sundayDate)}
                    </span>
                )}
                {view === "month" && (
                    <span>
                        {months[selectedMonth]} {selectedYear}
                    </span>
                )}
                {view === "year" && <span>{selectedYear}</span>}
                {view === "list" && <span>All Events</span>}
                <button onClick={() => navigatePeriod(1)}>
                    Next {view === "week" ? "Week" : view === "month" ? "Month" : "Year"}
                </button>
            </div>

            {/* Week View */}
            {view === "week" && (
                <>
                    {/* Mobile Day Navigation */}
                    <div className="mobile-day-navigation">
                        <button
                            onClick={() => setMobileWeekDayIndex(prev => Math.max(0, prev - 1))}
                            disabled={mobileWeekDayIndex === 0}
                            className="mobile-nav-btn"
                        >
                            ← Previous Day
                        </button>
                        <div className="mobile-day-info">
                            <span>{daysOfWeek[getWeekDays()[mobileWeekDayIndex]?.getDay() === 0 ? 6 : (getWeekDays()[mobileWeekDayIndex]?.getDay() || 1) - 1]} - {getWeekDays()[mobileWeekDayIndex]?.toLocaleDateString()}</span>
                        </div>
                        <button
                            onClick={() => setMobileWeekDayIndex(prev => Math.min(6, prev + 1))}
                            disabled={mobileWeekDayIndex === 6}
                            className="mobile-nav-btn"
                        >
                            Next Day →
                        </button>
                    </div>
                    <div className="calendar-container">
                        <div className="calendar">
                            <div className="calendar-header">
                                <div className="time-slot" />
                                {getWeekDays().map((date, index) => {
                                     // On mobile only, show only the selected day
                                     if (window.innerWidth <= 480 && index !== mobileWeekDayIndex) {
                                         return null;
                                     }
                                     
                                     const jsDay = date.getDay();
                                     const arrayIndex = jsDay === 0 ? 6 : jsDay - 1;
                                     const dayName = daysOfWeek[arrayIndex];

                                     return (
                                         <div
                                             className={`day-column-header ${isToday(date) ? "today" : ""
                                                 }`}
                                             key={date.toISOString()}
                                         >
                                             {dayName}
                                             <div className="date-label">{date.getDate()}</div>
                                         </div>
                                     );
                                 })}
                            </div>

                            {/* All-day events section */}
                            <div className="calendar-grid-allday">
                                <div className="time-slot">All Day</div>
                                {getWeekDays().map((date, index) => {
                                     // On mobile only, show only the selected day
                                     if (window.innerWidth <= 480 && index !== mobileWeekDayIndex) {
                                         return null;
                                     }
                                     
                                     const allDayEvents = getEventsForDay(date).filter(e => e.allDay);
                                     return (
                                         <div
                                             className={`day-column-allday ${isToday(date) ? "today" : ""}`}
                                             key={`allday-${date.toISOString()}`}
                                         >
                                            {allDayEvents.map((event, index) => (
                                                <div
                                                    key={`${event.id}-${event._id}-${index}`}
                                                    className="allday-event"
                                                    style={{
                                                        backgroundColor: event.color || colorOptions[0],
                                                        borderLeft: `4px solid ${(event.userColor || event.color || colorOptions[0])}`
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showEventDetails(event, e);
                                                    }}
                                                    title={`${event.course}\nby ${event.createdByName || 'Unknown'}`}
                                                >
                                                    <span className="allday-event-title">{event.course}</span>
                                                    {event.createdByName && (
                                                        <span className="allday-event-user">{event.createdByName}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="calendar-grid-continuous">
                                {/* Time labels */}
                                <div className="time-labels-column">
                                    {hours.map((hour, i) => (
                                        <div className="time-label-row" key={i}>
                                            <span className="time-label-text">{hour}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Day columns */}
                                {getWeekDays().map((date, index) => {
                                     // On mobile only, show only the selected day
                                     if (window.innerWidth <= 480 && index !== mobileWeekDayIndex) {
                                         return null;
                                     }
                                     
                                     return (
                                     <div
                                         className={`day-column-continuous ${isToday(date) ? "today" : ""}`}
                                         key={date.toISOString()}
                                     >
                                        {/* Hour grid background */}
                                        {hours.map((hour, i) => (
                                            <div className="hour-grid-line" key={i} />
                                        ))}

                                        {/* Continuous events */}
                                        {getEventsForDay(date).map((item, index) => {
                                            // Double safety check for undefined item
                                            if (!item) {
                                                console.warn("Item became undefined during mapping");
                                                return null;
                                            }

                                            // Ensure critical properties exist with fallbacks
                                            const safeItem = {
                                                ...item, // Keep all original properties first
                                                course: item.course || 'Untitled Event',
                                                color: item.color || colorOptions[0],
                                                date: item.date || new Date().toISOString().split("T")[0],
                                                id: item.id || item._id || `event-${index}`,
                                                _id: item._id || item.id || `event-${index}`,
                                            };

                                            const eventStyle = getContinuousEventStyle(safeItem, date, getEventsForDay(date));
                                            const eventStartMinutes = parseInt(safeItem.startTime?.split(":")[1], 10) || 0;
                                            const eventEndMinutes = parseInt(safeItem.endTime?.split(":")[1], 10) || 0;
                                            const hasNonExactTime = eventStartMinutes !== 0 || eventEndMinutes !== 0;

                                            // Check if this is an overnight event and which day we're viewing
                                            const viewKey = toLocalDateKey(date);
                                            const isOvernightOnStartDay = safeItem.isOvernightEvent &&
                                                safeItem.startTime && safeItem.endTime &&
                                                parseInt(safeItem.endTime.split(":")[0]) < parseInt(safeItem.startTime.split(":")[0]) &&
                                                viewKey === safeItem.date;

                                            const isOvernightOnNextDay = safeItem.isOvernightEvent &&
                                                safeItem.startTime && safeItem.endTime &&
                                                parseInt(safeItem.endTime.split(":")[0]) < parseInt(safeItem.startTime.split(":")[0]) &&
                                                viewKey !== safeItem.date;

                                            return (
                                                <div
                                                    className={`calendar-event ${safeItem.allDay ? 'all-day' : ''} ${safeItem.isMultiDay ? 'multiday' : ''} ${safeItem.recurring ? 'recurring' : ''} ${hasNonExactTime ? 'non-exact-time' : ''} ${isOvernightOnStartDay ? 'overnight-continues' : ''} ${isOvernightOnNextDay ? 'overnight-from-previous' : ''}`}
                                                    key={`${safeItem.id}-${index}`}
                                                    style={{
                                                        backgroundColor: safeItem.color,
                                                        ...eventStyle,
                                                        position: 'absolute',
                                                        left: '2px',
                                                        right: '2px'
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showEventDetails(safeItem, e);
                                                    }}
                                                    onContextMenu={(e) => {
                                                        if (safeItem.isMultiDay) {
                                                            e.preventDefault();
                                                            if (window.confirm(`Exclude ${formatDate(date)} from "${safeItem.course}"?`)) {
                                                                excludeDateFromEvent(safeItem._id || safeItem.id, toLocalDateKey(date));
                                                            }
                                                        }
                                                    }}
                                                    title={`${safeItem.course}${!safeItem.allDay && safeItem.startTime && safeItem.endTime ? '\n' + formatTime(safeItem.startTime) + ' - ' + formatTime(safeItem.endTime) : ''}`}
                                                >
                                                    <div className="event-content">
                                                        <div className="event-title">
                                                            {safeItem.userAvatar && <span className="event-user-avatar">{safeItem.userAvatar}</span>}
                                                            {safeItem.course}
                                                        </div>
                                                        {!safeItem.allDay && safeItem.startTime && safeItem.endTime && (
                                                            <div className="event-time-label" style={{
                                                                fontWeight: hasNonExactTime ? '600' : '500'
                                                            }}>
                                                                {formatTime(safeItem.startTime)} - {formatTime(safeItem.endTime)}
                                                            </div>
                                                        )}
                                                        {safeItem.createdByName && (
                                                            <div className="event-creator-label" title={`Created by ${safeItem.createdByName}`}>
                                                                {safeItem.createdByName}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {safeItem.isMultiDay && (
                                                        <button
                                                            className="exclude-day-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm(`Exclude ${formatDate(date)} from "${safeItem.course}"?`)) {
                                                                    excludeDateFromEvent(safeItem._id || safeItem.id, toLocalDateKey(date));
                                                                }
                                                            }}
                                                            title={`Exclude ${formatDate(date)} from this event`}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </>
            )}

      {/* Month View */}
      {view === "month" && (
        <div className="month-view">
                    <div className="month-grid">
                        {daysOfWeek.map((day) => (
                            <div className="month-header-cell" key={day}>
                                {day.substring(0, 3)}
                            </div>
                        ))}

                        {getDaysInMonth(selectedYear, selectedMonth).map((date, index) => (
                            <div
                                key={index}
                                className={`month-cell ${date && isToday(date) ? "today" : ""
                                    } ${!date ? "empty-cell" : ""}`}
                                onClick={() => date && setSelectedDate(date)}
                            >
                                {date && (
                                    <>
                                        <div className="month-date-number">{date.getDate()}</div>
                                        <div className="month-events">
                                            {getEventsForDate(date)
                                                .slice(0, 3)
                                                .map((event, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="month-event"
                                                        style={{ backgroundColor: event.color }}
                                                        title={`${event.course}${event.allDay
                                                                ? " (All day)"
                                                                : (event.startTime && event.endTime
                                                                    ? ` (${formatTime(event.startTime)} - ${formatTime(event.endTime)})`
                                                                    : " (Time not set)")
                                                            }${event.isMultiDay && event.endDate
                                                                ? ` - Multi-day: ${formatDate(new Date(event.date))} to ${formatDate(new Date(event.endDate))}`
                                                                : ""
                                                            }${event.description ? ": " + event.description : ""
                                                            }`}
                                                    >
                                                        {event.course}
                                                    </div>
                                                ))}
                                            {getEventsForDate(date).length > 3 && (
                                                <div className="more-events">
                                                    +{getEventsForDate(date).length - 3} more
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Year View */}
            {view === "year" && (
                <div className="year-view">
                    {months.map((month, idx) => (
                        <div
                            key={idx}
                            className="year-month"
                            onClick={() => {
                                setSelectedMonth(idx);
                                setView("month");
                            }}
                        >
                            <h3>{month}</h3>
                            <div className="mini-month-grid">
                                {Array.from({ length: 7 }, (_, i) => (
                                    <div key={i} className="mini-month-header">
                                        {daysOfWeek[i].charAt(0)}
                                    </div>
                                ))}

                                {getDaysInMonth(selectedYear, idx).map((date, dateIdx) => (
                                    <div
                                        key={dateIdx}
                                        className={`mini-month-cell ${date && isToday(date) ? "today" : ""
                                            } ${!date ? "empty-cell" : ""}`}
                                    >
                                        {date && date.getDate()}
                                        {date && getEventsForDate(date).length > 0 && (
                                            <div className="mini-month-indicator"></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* List View */}
            {view === "list" && (
                <div className="list-view">
                    {schedule.length === 0 ? (
                        <p>No events scheduled yet.</p>
                    ) : (
                        <>
                            <div className="list-filters">
                                <input
                                    type="text"
                                    placeholder="Search events by name or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="search-input"
                                />
                                <div className="filter-buttons">
                                    <button
                                        className={`filter-btn ${listViewFilter === "upcoming" ? "active" : ""}`}
                                        onClick={() => setListViewFilter("upcoming")}
                                    >
                                        Upcoming Events
                                    </button>
                                    <button
                                        className={`filter-btn ${listViewFilter === "all" ? "active" : ""}`}
                                        onClick={() => setListViewFilter("all")}
                                    >
                                        All Events
                                    </button>
                                </div>

                                <div className="user-filter-section">
                                    <label htmlFor="user-dropdown" className="filter-label">Filter by User:</label>
                                    <select
                                        id="user-dropdown"
                                        className="user-dropdown"
                                        value={selectedListUser || "all"}
                                        onChange={(e) => setSelectedListUser(e.target.value === "all" ? null : e.target.value)}
                                    >
                                        <option value="all">All Users</option>
                                        {predefinedUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.avatar} {user.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {predefinedUsers.filter(user => {
                                if (!visibleUsers.includes(user.id)) return false;
                                if (selectedListUser === null) return true; // Show all if null
                                return user.id === selectedListUser; // Show only selected user
                            }).map(user => {
                                const today = toLocalDateKey(new Date());
                                let userEvents = schedule
                                    .filter(event => event.createdBy === user.id)
                                    .sort((a, b) => a.date.localeCompare(b.date));

                                // Filter by upcoming/all
                                if (listViewFilter === "upcoming") {
                                    userEvents = userEvents.filter(event => event.date >= today);
                                }

                                // Filter by search query
                                if (searchQuery.trim()) {
                                    const query = searchQuery.toLowerCase();
                                    userEvents = userEvents.filter(event =>
                                        event.course?.toLowerCase().includes(query) ||
                                        event.description?.toLowerCase().includes(query)
                                    );
                                }

                                if (userEvents.length === 0) return null;

                                const isExpanded = expandedUsers.has(user.id);

                                const toggleUserExpand = (userId) => {
                                    const newExpanded = new Set(expandedUsers);
                                    if (newExpanded.has(userId)) {
                                        newExpanded.delete(userId);
                                    } else {
                                        newExpanded.add(userId);
                                    }
                                    setExpandedUsers(newExpanded);
                                };

                                return (
                                    <div key={user.id} className="user-events-section">
                                        <button
                                            className="user-section-header"
                                            onClick={() => toggleUserExpand(user.id)}
                                            style={{
                                                backgroundColor: user.color,
                                                color: 'white',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                marginTop: '20px',
                                                marginBottom: '12px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                width: '100%',
                                                textAlign: 'left',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                fontSize: '1rem',
                                                fontWeight: '600',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <span>{user.avatar} {user.name}'s Events ({userEvents.length})</span>
                                            <span className="dropdown-arrow" style={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                                display: 'inline-block',
                                                marginLeft: '8px'
                                            }}>▼</span>
                                        </button>

                                        {isExpanded && userEvents.map((event) => {
                                            return (
                                                <div
                                                    className="event-item"
                                                    key={event._id || event.id}
                                                    style={{ borderLeft: `5px solid ${event.color}` }}
                                                >
                                                    <div className="event-header">
                                                        <h3>{event.course}</h3>
                                                        <div className="event-actions">
                                                            <button
                                                                className="edit-event"
                                                                onClick={() => editEvent(event)}
                                                                title="Edit event"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="remove-event"
                                                                onClick={() => removeEvent(event._id || event.id)}
                                                                title="Remove event"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p className="event-date">
                                                        {event.isMultiDay && event.endDate
                                                            ? `${formatDateString(event.date)} - ${formatDateString(event.endDate)}${event.excludedDates && event.excludedDates.length > 0
                                                                ? ` (${event.excludedDates.length} day${event.excludedDates.length !== 1 ? 's' : ''} excluded)`
                                                                : ""
                                                            }`
                                                            : formatDateString(event.date)
                                                        }
                                                        {event.recurring && event.isMultiDay && (
                                                            event.repeatLimited && event.recurrenceType === "weekly"
                                                                ? ` (Multiday event repeats ${event.recurrenceType} for ${event.repeatWeeks} weeks)`
                                                                : ` (Multiday event repeats ${event.recurrenceType})`
                                                        )}
                                                        {event.recurring && !event.isMultiDay && (
                                                            event.repeatLimited && event.recurrenceType === "weekly"
                                                                ? ` (Repeats ${event.recurrenceType} for ${event.repeatWeeks} weeks)`
                                                                : ` (Repeats ${event.recurrenceType})`
                                                        )}
                                                        {event.isMultiDay && !event.recurring && " (Multi-day)"}
                                                    </p>
                                                    <p className="event-time">
                                                        {event.allDay
                                                            ? "All day"
                                                            : (event.startTime && event.endTime
                                                                ? `${formatTime(event.startTime)} - ${formatTime(event.endTime)}${event.isOvernightEvent ? " (overnight)" : ""
                                                                }`
                                                                : "Time not set")
                                                        }
                                                    </p>
                                                    {event.description && (
                                                        <p className="event-description">{event.description}</p>
                                                    )}

                                                    {event.isMultiDay && event.excludedDates && event.excludedDates.length > 0 && (
                                                        <div className="excluded-dates">
                                                            <p className="excluded-dates-label">Excluded dates:</p>
                                                            <div className="excluded-dates-list">
                                                                {event.excludedDates.map((excludedDate, idx) => (
                                                                    <span key={idx} className="excluded-date-chip">
                                                                        {formatDate(new Date(excludedDate))}
                                                                        <button
                                                                            className="restore-date-btn"
                                                                            onClick={() => restoreDateToEvent(event._id || event.id, excludedDate)}
                                                                            title="Restore this date"
                                                                        >
                                                                            ↻
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}

                            {/* Show events without a user (legacy events) */}
                            {schedule.filter(event => !event.createdBy &&
                                (!event.createdBy || visibleUsers.includes(event.createdBy))).length > 0 && (
                                    <div className="user-events-section">
                                        <h3 className="user-section-header" style={{
                                            backgroundColor: '#64748b',
                                            color: 'white',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            marginTop: '20px',
                                            marginBottom: '12px'
                                        }}>
                                            📅 Unassigned Events ({schedule.filter(e => !e.createdBy).length})
                                        </h3>

                                        {schedule
                                            .filter(event => !event.createdBy)
                                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                                            .map((event) => (
                                                <div
                                                    className="event-item"
                                                    key={event._id || event.id}
                                                    style={{ borderLeft: `5px solid ${event.color}` }}
                                                >
                                                    <div className="event-header">
                                                        <h3>{event.course}</h3>
                                                        <div className="event-actions">
                                                            <button
                                                                className="edit-event"
                                                                onClick={() => editEvent(event)}
                                                                title="Edit event"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="remove-event"
                                                                onClick={() => removeEvent(event._id || event.id)}
                                                                title="Remove event"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <p className="event-date">
                                                        {event.isMultiDay && event.endDate
                                                            ? `${formatDate(new Date(event.date))} - ${formatDate(new Date(event.endDate))}`
                                                            : formatDate(new Date(event.date))
                                                        }
                                                    </p>
                                                    <p className="event-time">
                                                        {event.allDay
                                                            ? "All day"
                                                            : (event.startTime && event.endTime
                                                                ? `${formatTime(event.startTime)} - ${formatTime(event.endTime)}${event.isOvernightEvent ? " (overnight)" : ""
                                                                }`
                                                                : "Time not set")
                                                        }
                                                    </p>
                                                    {event.description && (
                                                        <p className="event-description">{event.description}</p>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                )}
                        </>
                    )}
                </div>
            )}

            {/* Event Details Popup */}
            {eventDetailsPopup.show && eventDetailsPopup.event && (
                <div
                    className="event-details-popup"
                    style={{
                        position: 'fixed',
                        left: eventDetailsPopup.position.x,
                        top: eventDetailsPopup.position.y,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 1000,
                    }}
                    onMouseEnter={() => { }} // Keep popup open when hovering over it
                    onMouseLeave={hideEventDetails}
                >
                    <div className="event-details-content">
                        <h4 className="event-details-title">{eventDetailsPopup.event.course || 'Untitled Event'}</h4>

                        <div className="event-details-info">
                            <div className="event-detail-row">
                                <span className="detail-label">📅 Date:</span>
                                <span className="detail-value">
                                    {eventDetailsPopup.event.isMultiDay && eventDetailsPopup.event.endDate
                                        ? `${formatDate(new Date(eventDetailsPopup.event.date))} - ${formatDate(new Date(eventDetailsPopup.event.endDate))}`
                                        : formatDate(new Date(eventDetailsPopup.event.date))
                                    }
                                </span>
                            </div>

                            <div className="event-detail-row">
                                <span className="detail-label">⏰ Time:</span>
                                <span className="detail-value" style={{
                                    fontWeight: (() => {
                                        if (eventDetailsPopup.event.allDay) return 'normal';
                                        const startMin = parseInt(eventDetailsPopup.event.startTime?.split(':')[1], 10) || 0;
                                        const endMin = parseInt(eventDetailsPopup.event.endTime?.split(':')[1], 10) || 0;
                                        return (startMin !== 0 || endMin !== 0) ? '600' : 'normal';
                                    })(),
                                    color: (() => {
                                        if (eventDetailsPopup.event.allDay) return 'inherit';
                                        const startMin = parseInt(eventDetailsPopup.event.startTime?.split(':')[1], 10) || 0;
                                        const endMin = parseInt(eventDetailsPopup.event.endTime?.split(':')[1], 10) || 0;
                                        return (startMin !== 0 || endMin !== 0) ? 'var(--primary-color)' : 'inherit';
                                    })()
                                }}>
                                    {eventDetailsPopup.event.allDay
                                        ? "All day"
                                        : (eventDetailsPopup.event.startTime && eventDetailsPopup.event.endTime
                                            ? `${formatTime(eventDetailsPopup.event.startTime)} - ${formatTime(eventDetailsPopup.event.endTime)}${eventDetailsPopup.event.isOvernightEvent ? " (overnight)" : ""
                                            }`
                                            : "Time not set")
                                    }
                                </span>
                            </div>

                            {eventDetailsPopup.event.description && (
                                <div className="event-detail-row">
                                    <span className="detail-label">📝 Description:</span>
                                    <span className="detail-value">{eventDetailsPopup.event.description}</span>
                                </div>
                            )}

                            {eventDetailsPopup.event.recurring && (
                                <div className="event-detail-row">
                                    <span className="detail-label">🔄 Recurring:</span>
                                    <span className="detail-value">
                                        {eventDetailsPopup.event.recurrenceType}
                                        {eventDetailsPopup.event.repeatLimited && eventDetailsPopup.event.repeatWeeks
                                            ? ` (${eventDetailsPopup.event.repeatWeeks} weeks)`
                                            : ""
                                        }
                                    </span>
                                </div>
                            )}

                            {eventDetailsPopup.event.isMultiDay && (
                                <div className="event-detail-row">
                                    <span className="detail-label">📆 Type:</span>
                                    <span className="detail-value">Multi-day event</span>
                                </div>
                            )}
                        </div>

                        <div className="event-details-actions">
                            <button
                                className="btn btn-small btn-primary"
                                onClick={() => {
                                    hideEventDetails();
                                    editEvent(eventDetailsPopup.event);
                                }}
                            >
                                ✏️ Edit
                            </button>
                            <button
                                className="btn btn-small btn-danger"
                                onClick={() => {
                                    hideEventDetails();
                                    removeEvent(eventDetailsPopup.event._id || eventDetailsPopup.event.id);
                                }}
                            >
                                🗑️ Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
